/**
 * =============================================================================
 * TEMA CLARO / OSCURO
 * =============================================================================
 * `Theme` es lo que el usuario ELIGE: claro, oscuro, o "que decida el sistema".
 * El tema RESUELTO ('light' | 'dark') es lo que realmente se aplica: se calcula
 * a partir de la elección y, si es "system", de `prefers-color-scheme`.
 *
 * La clase `dark` se pone en `<html>`. `globals.css` la usa vía
 * `@custom-variant dark` para redefinir los tokens de color.
 *
 * El primer frame lo resuelve un script inline en `index.html` (sin parpadeo);
 * a partir de ahí manda este módulo, y `themeStore` es la API para la UI.
 * =============================================================================
 */

export type Theme = 'light' | 'dark' | 'system'

export const THEME_STORAGE_KEY = 'alto-palermo-theme'

const prefersDark = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches

/** Traduce la elección del usuario al tema que de verdad se pinta. */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') return prefersDark() ? 'dark' : 'light'
  return theme
}

/** Lee la elección guardada. Cualquier valor inesperado cae en 'system'. */
export function readStoredTheme(): Theme {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY)
    if (value === 'light' || value === 'dark' || value === 'system') return value
  } catch {
    // localStorage puede lanzar en modo privado / con cookies bloqueadas.
  }
  return 'system'
}

/** Persiste la elección. Silencioso si localStorage no está disponible. */
export function storeTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    /* no-op */
  }
}

/** Aplica (o quita) la clase `dark` y ajusta `color-scheme` para los nativos. */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return
  const resolved = resolveTheme(theme)
  document.documentElement.classList.toggle('dark', resolved === 'dark')
  document.documentElement.style.colorScheme = resolved
}

/**
 * Reacciona a que el usuario cambie el tema de su sistema operativo mientras la
 * app está abierta, pero solo cuando su elección es "system". Se registra una
 * sola vez al importar el módulo.
 */
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (readStoredTheme() === 'system') applyTheme('system')
  })
}
